import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentType,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import type { BoardTemplateId } from "@bezpart-board/shared";
import { isBoardToolId, type BoardToolId } from "../../editor/types";
import { hasInternalClipboard, subscribeInternalClipboard } from "../../editor/clipboard";
import { ENABLED_BOARD_TOOLS } from "../../editor/toolRegistry";
import {
  getToolShortcutLabel,
  subscribeBoardKeybindings,
  getToolKeyBindings,
  TOOL_KEYBINDING_LABELS,
} from "../../editor/boardKeybindings";
import {
  getLastShapesGroupTool,
  isShapesGroupRailHidden,
  isShapesGroupTool,
  setLastShapesGroupTool,
  type ShapesGroupToolId,
} from "../../editor/shapesGroup";
import { BoardPenOptions } from "./BoardPenOptions";
import { BoardShapeFlyout } from "./BoardShapeFlyout";
import { BoardShapeOptions } from "./BoardShapeOptions";
import { BoardEraserOptions } from "./BoardEraserOptions";
import { BoardStickyOptions } from "./BoardStickyOptions";
import { BoardImageButton } from "./BoardImageButton";
import { BoardPdfButton } from "./BoardPdfButton";
import { BoardAudioButton } from "./BoardAudioButton";
import { BoardVoiceButton } from "./BoardVoiceButton";
import { BoardWidgetsButton } from "./BoardWidgetsButton";
import { BoardWidgetsFlyout } from "./BoardWidgetsFlyout";
import {
  IconCursor,
  IconHand,
  IconPen,
  IconEraser,
  IconLine,
  IconArrow,
  IconRect,
  IconFrame,
  IconText,
  IconSticky,
} from "./BoardToolIcons";

type Props = {
  tool: BoardToolId;
  onToolChange: (tool: BoardToolId) => void;
  canEdit: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  /** Вставка из буфера доски — для планшетов, где нет Ctrl+V. */
  onPaste?: () => void;
  onImageFile?: (file: File) => void;
  onPdfFile?: (file: File) => void;
  onAudioFile?: (file: File) => void;
  onVoiceError?: (message: string) => void;
  onInsertWidget?: (templateId: BoardTemplateId) => void;
  onInsertTimer?: () => void;
  onInsertCalculator?: () => void;
  onInsertGraphUnits?: () => void;
  onInsertCode?: () => void;
  onInsertSql?: () => void;
  onInsertPoll?: () => void;
  pdfInputRef?: React.RefObject<HTMLInputElement | null>;
};

const TOOL_ICONS: Record<BoardToolId, ComponentType> = {
  select: IconCursor,
  hand: IconHand,
  pen: IconPen,
  line: IconLine,
  arrow: IconArrow,
  shape: IconRect,
  frame: IconFrame,
  text: IconText,
  sticky: IconSticky,
  eraser: IconEraser,
};

const TOOLS_WITH_OPTIONS: BoardToolId[] = ["pen", "line", "arrow", "shape", "sticky", "eraser"];

function hasToolOptions(tool: BoardToolId): boolean {
  return TOOLS_WITH_OPTIONS.includes(tool);
}

function optionsAnchorTool(tool: BoardToolId): BoardToolId {
  return isShapesGroupTool(tool) ? "shape" : tool;
}

type PopoverPos = { top: number; left: number };
const TOOL_ORDER_STORAGE_KEY = "wb_tool_order_v1";
/** Сколько держать иконку, прежде чем начнётся перетаскивание (быстрый клик его не запускает). */
const TOOL_DRAG_HOLD_MS = 600;
/** Сдвиг до срабатывания удержания, который отменяет перетаскивание. */
const TOOL_DRAG_CANCEL_PX = 6;

type ToolPress = {
  id: BoardToolId;
  pointerId: number;
  startX: number;
  startY: number;
  el: HTMLButtonElement;
  timer: number;
  dragging: boolean;
  moved: boolean;
};

function clampPopover(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function readVisualViewport(): { left: number; top: number; width: number; height: number } {
  const vv = window.visualViewport;
  return {
    left: vv?.offsetLeft ?? 0,
    top: vv?.offsetTop ?? 0,
    width: vv?.width ?? window.innerWidth,
    height: vv?.height ?? window.innerHeight,
  };
}

/** Поповер справа от кнопки; по вертикали — по центру кнопки, но строго в пределах экрана. */
function computeToolPopoverPos(
  anchorRect: DOMRect,
  popW: number,
  popH: number
): PopoverPos {
  const gap = 8;
  const margin = 8;
  const view = readVisualViewport();
  const viewR = view.left + view.width;
  const viewB = view.top + view.height;

  let left = anchorRect.right + gap;
  if (left + popW > viewR - margin) {
    left = anchorRect.left - gap - popW;
  }
  left = clampPopover(left, view.left + margin, Math.max(view.left + margin, viewR - popW - margin));

  const minTop = view.top + margin;
  const maxTop = Math.max(minTop, viewB - popH - margin);
  const centeredTop = anchorRect.top + (anchorRect.height - popH) / 2;
  const top = clampPopover(centeredTop, minTop, maxTop);

  return { left, top };
}

function optionsLabel(tool: BoardToolId): string {
  if (tool === "pen") return "Параметры ручки";
  if (isShapesGroupTool(tool)) return "Фигуры";
  if (tool === "sticky") return "Цвет стикера";
  if (tool === "eraser") return "Размер ластика";
  return "Параметры инструмента";
}

export function BoardFloatingToolbar({
  tool,
  onToolChange,
  canEdit,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  onPaste,
  onImageFile,
  onPdfFile,
  onAudioFile,
  onVoiceError,
  onInsertWidget,
  onInsertTimer,
  onInsertCalculator,
  onInsertGraphUnits,
  onInsertCode,
  onInsertSql,
  onInsertPoll,
  pdfInputRef,
}: Props) {
  const enabledToolIds = useMemo(
    () => ENABLED_BOARD_TOOLS.map((def) => def.id),
    []
  );
  // Кнопка вставки появляется, как только что-то скопировали.
  const clipboardReady = useSyncExternalStore(
    subscribeInternalClipboard,
    hasInternalClipboard,
    hasInternalClipboard
  );
  const [toolOrder, setToolOrder] = useState<BoardToolId[]>(enabledToolIds);
  const [dragToolId, setDragToolId] = useState<BoardToolId | null>(null);
  const [dropToolId, setDropToolId] = useState<BoardToolId | null>(null);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const toolPressRef = useRef<ToolPress | null>(null);
  const suppressToolClickRef = useRef(false);
  const [optionsOpen, setOptionsOpen] = useState(true);
  const [variantOpen, setVariantOpen] = useState(false);
  const [widgetsOpen, setWidgetsOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState<PopoverPos | null>(null);
  const toolBtnRefs = useRef<Partial<Record<BoardToolId, HTMLButtonElement | null>>>({});
  const popoverRef = useRef<HTMLDivElement>(null);
  const widgetsBtnRef = useRef<HTMLButtonElement | null>(null);
  const widgetsPopoverRef = useRef<HTMLDivElement>(null);
  const [widgetsPos, setWidgetsPos] = useState<PopoverPos | null>(null);

  useSyncExternalStore(subscribeBoardKeybindings, getToolKeyBindings, getToolKeyBindings);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(TOOL_ORDER_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      const incoming = parsed.filter((v): v is BoardToolId => typeof v === "string" && isBoardToolId(v));
      const unique: BoardToolId[] = [];
      for (const id of incoming) {
        if (!enabledToolIds.includes(id)) continue;
        if (!unique.includes(id)) unique.push(id);
      }
      for (const id of enabledToolIds) {
        if (!unique.includes(id)) unique.push(id);
      }
      if (unique.length) setToolOrder(unique);
    } catch {
      /* ignore broken storage */
    }
  }, [enabledToolIds]);

  useEffect(() => {
    try {
      localStorage.setItem(TOOL_ORDER_STORAGE_KEY, JSON.stringify(toolOrder));
    } catch {
      /* ignore quota/privacy errors */
    }
  }, [toolOrder]);

  const orderedTools = useMemo(() => {
    const byId = new Map(ENABLED_BOARD_TOOLS.map((def) => [def.id, def]));
    const ordered = toolOrder
      .map((id) => byId.get(id))
      .filter((def): def is (typeof ENABLED_BOARD_TOOLS)[number] => Boolean(def));
    for (const def of ENABLED_BOARD_TOOLS) {
      if (!ordered.some((x) => x.id === def.id)) ordered.push(def);
    }
    return ordered;
  }, [toolOrder]);

  useEffect(() => {
    if (isShapesGroupTool(tool)) {
      setLastShapesGroupTool(tool);
      setWidgetsOpen(false);
      return;
    }
    setOptionsOpen(hasToolOptions(tool));
    setVariantOpen(false);
    setWidgetsOpen(false);
  }, [tool]);

  const updatePopoverPos = useCallback(() => {
    if (!optionsOpen || !hasToolOptions(tool)) {
      setPopoverPos(null);
      return;
    }
    const btn = toolBtnRefs.current[optionsAnchorTool(tool)];
    if (!btn) {
      setPopoverPos(null);
      return;
    }
    const rect = btn.getBoundingClientRect();
    const view = readVisualViewport();
    const popEl = popoverRef.current;
    const popW = popEl ? popEl.offsetWidth : Math.min(280, view.width - 16);
    const popH = popEl
      ? popEl.offsetHeight
      : isShapesGroupTool(tool)
        ? Math.min(480, Math.round(view.height * 0.65))
        : 160;
    setPopoverPos(computeToolPopoverPos(rect, popW, popH));
  }, [optionsOpen, tool]);

  const updateWidgetsPos = useCallback(() => {
    if (!widgetsOpen || !widgetsBtnRef.current) {
      setWidgetsPos(null);
      return;
    }
    const rect = widgetsBtnRef.current.getBoundingClientRect();
    const view = readVisualViewport();
    const popEl = widgetsPopoverRef.current;
    const popW = popEl ? popEl.offsetWidth : Math.min(280, view.width - 16);
    const popH = popEl ? popEl.offsetHeight : 200;
    setWidgetsPos(computeToolPopoverPos(rect, popW, popH));
  }, [widgetsOpen]);

  useLayoutEffect(() => {
    updatePopoverPos();
    if (!optionsOpen) return;
    const el = popoverRef.current;
    const id = requestAnimationFrame(() => updatePopoverPos());
    if (!el || typeof ResizeObserver === "undefined") {
      return () => cancelAnimationFrame(id);
    }
    const ro = new ResizeObserver(() => updatePopoverPos());
    ro.observe(el);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(id);
    };
  }, [updatePopoverPos, optionsOpen, tool]);

  useLayoutEffect(() => {
    updateWidgetsPos();
    if (!widgetsOpen) return;
    const el = widgetsPopoverRef.current;
    const id = requestAnimationFrame(() => updateWidgetsPos());
    if (!el || typeof ResizeObserver === "undefined") {
      return () => cancelAnimationFrame(id);
    }
    const ro = new ResizeObserver(() => updateWidgetsPos());
    ro.observe(el);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(id);
    };
  }, [updateWidgetsPos, widgetsOpen]);

  useEffect(() => {
    if (!optionsOpen && !widgetsOpen) return;
    function onLayoutChange() {
      requestAnimationFrame(() => {
        updatePopoverPos();
        updateWidgetsPos();
      });
    }
    function onResize() {
      updatePopoverPos();
      updateWidgetsPos();
    }
    window.addEventListener("wb:tool-options-layout", onLayoutChange);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", onResize);
    vv?.addEventListener("scroll", onResize);
    return () => {
      window.removeEventListener("wb:tool-options-layout", onLayoutChange);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
      vv?.removeEventListener("resize", onResize);
      vv?.removeEventListener("scroll", onResize);
    };
  }, [optionsOpen, widgetsOpen, updatePopoverPos, updateWidgetsPos]);

  useEffect(() => {
    if (!optionsOpen && !widgetsOpen) return;
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") {
        setOptionsOpen(false);
        setVariantOpen(false);
        setWidgetsOpen(false);
      }
    }
    function onDoc(ev: PointerEvent) {
      const target = ev.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (widgetsPopoverRef.current?.contains(target)) return;
      const activeBtn = toolBtnRefs.current[optionsAnchorTool(tool)];
      if (activeBtn?.contains(target)) return;
      if (widgetsBtnRef.current?.contains(target)) return;
      // Окошко выбора цвета висит в портале у самой точки: формально это
      // «мимо панели», но закрывать её там нельзя — вместе с ней пропадут
      // и сами точки, из которых окошко открыли.
      if (target instanceof Element && target.closest(".wb-color-pop")) return;
      // Обучение рассказывает как раз про эту панель и показывает на неё.
      // Его собственные кнопки — «Дальше», «Назад» — формально тоже «мимо»,
      // и панель закрывалась от каждого нажатия: шаг начинался с закрытой
      // панелью, обучение открывало её обратно, и карточка появлялась
      // дважды — сперва у кнопки пера, потом у палитры.
      if (target instanceof Element && target.closest(".wb-tour")) return;
      setOptionsOpen(false);
      setVariantOpen(false);
      setWidgetsOpen(false);
    }
    document.addEventListener("keydown", onKey);
    // capture=true: закрываем даже если канвас останавливает bubbling.
    document.addEventListener("pointerdown", onDoc, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDoc, true);
    };
  }, [optionsOpen, widgetsOpen, tool]);

  // Режим просмотра: только «рука» — сдвиг холста, без панели рисования.
  const moveTool = useCallback((from: BoardToolId, to: BoardToolId) => {
    if (from === to) return;
    setToolOrder((prev) => {
      const fromIdx = prev.indexOf(from);
      const toIdx = prev.indexOf(to);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const next = [...prev];
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
  }, []);

  /**
   * Ближайшая по вертикали иконка панели — цель перетаскивания.
   * Саму перетаскиваемую иконку пропускаем: она едет за указателем и иначе
   * всегда оказывалась бы ближайшей к нему.
   */
  const toolAtClientY = useCallback(
    (clientY: number, skipId: BoardToolId): BoardToolId | null => {
      let bestId: BoardToolId | null = null;
      let bestDist = Number.POSITIVE_INFINITY;
      for (const [rawId, el] of Object.entries(toolBtnRefs.current)) {
        if (!el || !el.isConnected || !isBoardToolId(rawId)) continue;
        if (rawId === skipId) continue;
        const rect = el.getBoundingClientRect();
        const dist = Math.abs(rect.top + rect.height / 2 - clientY);
        if (dist < bestDist) {
          bestDist = dist;
          bestId = rawId;
        }
      }
      return bestId;
    },
    []
  );

  const clearToolPress = useCallback(() => {
    const press = toolPressRef.current;
    if (press) {
      window.clearTimeout(press.timer);
      if (press.el.hasPointerCapture?.(press.pointerId)) {
        try {
          press.el.releasePointerCapture(press.pointerId);
        } catch {
          /* pointer уже отпущен */
        }
      }
    }
    toolPressRef.current = null;
    setDragToolId(null);
    setDropToolId(null);
    setDragOffsetY(0);
  }, []);

  useEffect(() => {
    const onMove = (ev: PointerEvent) => {
      const press = toolPressRef.current;
      if (!press || ev.pointerId !== press.pointerId) return;
      if (!press.dragging) {
        // До срабатывания удержания любой заметный сдвиг отменяет затею —
        // человек просто кликнул и повёл мышью.
        if (
          Math.abs(ev.clientY - press.startY) > TOOL_DRAG_CANCEL_PX ||
          Math.abs(ev.clientX - press.startX) > TOOL_DRAG_CANCEL_PX
        ) {
          clearToolPress();
        }
        return;
      }
      ev.preventDefault();
      press.moved = true;
      // Смещаем только по вертикали: вбок иконка не уезжает.
      setDragOffsetY(ev.clientY - press.startY);
      const over = toolAtClientY(ev.clientY, press.id);
      setDropToolId(over ?? press.id);
    };
    const onUp = (ev: PointerEvent) => {
      const press = toolPressRef.current;
      if (!press || ev.pointerId !== press.pointerId) return;
      if (press.dragging) {
        const over = toolAtClientY(ev.clientY, press.id);
        if (over) moveTool(press.id, over);
        if (press.moved) suppressToolClickRef.current = true;
      }
      clearToolPress();
    };
    const onCancel = (ev: PointerEvent) => {
      const press = toolPressRef.current;
      if (!press || ev.pointerId !== press.pointerId) return;
      clearToolPress();
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      clearToolPress();
    };
  }, [clearToolPress, moveTool, toolAtClientY]);

  const handleToolPointerDown = useCallback(
    (ev: ReactPointerEvent<HTMLButtonElement>, id: BoardToolId) => {
      if (ev.button !== 0) return;
      // Флаг живёт ровно до click текущего нажатия; если click не пришёл
      // (указатель отпустили вне окна), новое нажатие его сбрасывает.
      suppressToolClickRef.current = false;
      clearToolPress();
      const el = ev.currentTarget;
      const pointerId = ev.pointerId;
      const press: ToolPress = {
        id,
        pointerId,
        startX: ev.clientX,
        startY: ev.clientY,
        el,
        dragging: false,
        moved: false,
        timer: window.setTimeout(() => {
          const current = toolPressRef.current;
          if (!current || current.pointerId !== pointerId) return;
          current.dragging = true;
          try {
            el.setPointerCapture(pointerId);
          } catch {
            /* указатель уже отпущен */
          }
          setDragToolId(id);
          setDropToolId(id);
          setDragOffsetY(0);
        }, TOOL_DRAG_HOLD_MS),
      };
      toolPressRef.current = press;
    },
    [clearToolPress]
  );

  if (!canEdit) {
    return (
      <div className="wb-floating-tools wb-floating-tools--view-only" role="presentation">
        <div className="wb-floating-tools__main">
          <nav className="wb-tool-rail wb-tool-rail--primary" aria-label="Просмотр доски">
            <button
              type="button"
              className={`wb-tool-icon-btn${tool === "hand" ? " is-active" : ""}`}
              title="Рука — сдвиг доски"
              aria-label="Рука — сдвиг доски"
              aria-pressed={tool === "hand"}
              onClick={() => onToolChange("hand")}
            >
              <IconHand />
            </button>
          </nav>
        </div>
      </div>
    );
  }

  function handleToolClick(id: BoardToolId) {
    if (id === "shape") {
      const next: ShapesGroupToolId = isShapesGroupTool(tool)
        ? tool
        : getLastShapesGroupTool();
      onToolChange(next);
      if (isShapesGroupTool(tool) && hasToolOptions(tool)) {
        setOptionsOpen((open) => !open);
        setVariantOpen((open) => !open);
      } else {
        setOptionsOpen(true);
        setVariantOpen(true);
      }
      setWidgetsOpen(false);
      return;
    }
    onToolChange(id);
    if (id === tool && hasToolOptions(id)) {
      setOptionsOpen((open) => !open);
      return;
    }
    setOptionsOpen(true);
    setVariantOpen(false);
    setWidgetsOpen(false);
  }

  function pickShapesGroupTool(id: ShapesGroupToolId) {
    setLastShapesGroupTool(id);
    onToolChange(id);
    setOptionsOpen(false);
    setVariantOpen(false);
    setWidgetsOpen(false);
  }

  const showOptions = hasToolOptions(tool) && optionsOpen && popoverPos;

  return (
    <div className="wb-floating-tools" role="presentation">
      <div className="wb-floating-tools__main">
        <nav className="wb-tool-rail wb-tool-rail--history" aria-label="История">
          <button
            type="button"
            className="wb-tool-icon-btn"
            title="Отменить (Ctrl+Z)"
            aria-label="Отменить"
            disabled={!canUndo}
            onClick={() => onUndo?.()}
          >
            ↶
          </button>
          <button
            type="button"
            className="wb-tool-icon-btn"
            title="Повторить (Ctrl+Y)"
            aria-label="Повторить"
            disabled={!canRedo}
            onClick={() => onRedo?.()}
          >
            ↷
          </button>
          {canEdit && onPaste && clipboardReady ? (
            <button
              type="button"
              className="wb-tool-icon-btn"
              title="Вставить скопированное (Ctrl+V)"
              aria-label="Вставить скопированное"
              onClick={() => onPaste()}
            >
              ⎘
            </button>
          ) : null}
          {onImageFile ? <BoardImageButton onPickFile={onImageFile} /> : null}
          {onPdfFile ? <BoardPdfButton ref={pdfInputRef} onPickFile={onPdfFile} /> : null}
          {onAudioFile ? <BoardAudioButton onPickFile={onAudioFile} /> : null}
          {onAudioFile ? (
            <BoardVoiceButton onRecorded={onAudioFile} onError={onVoiceError} />
          ) : null}
          {onInsertWidget ||
          onInsertTimer ||
          onInsertCalculator ||
          onInsertGraphUnits ||
          onInsertCode ||
          onInsertSql ||
          onInsertPoll ? (
            <BoardWidgetsButton
              ref={widgetsBtnRef}
              active={widgetsOpen}
              onClick={() => {
                setWidgetsOpen((open) => !open);
                setOptionsOpen(false);
                setVariantOpen(false);
              }}
            />
          ) : null}
        </nav>
        <nav
          className={`wb-tool-rail wb-tool-rail--primary${
            dragToolId ? " is-reordering" : ""
          }`}
          aria-label="Инструменты"
          data-testid="board-tools"
        >
          {orderedTools
            .filter((def) => !isShapesGroupRailHidden(def.id))
            .map((def) => {
            const Icon =
              def.id === "shape" && isShapesGroupTool(tool)
                ? TOOL_ICONS[tool]
                : TOOL_ICONS[def.id];
            const shortcutLabel = getToolShortcutLabel(def.id);
            const isActive =
              def.id === "shape" ? isShapesGroupTool(tool) : tool === def.id;
            const title =
              def.id === "shape"
                ? `Фигуры — прямая, стрелка, прямоугольник… (${shortcutLabel})`
                : def.id === "select"
                  ? `Курсор — выделение и перенос (${shortcutLabel}). ПКМ или средняя кнопка — сдвиг холста`
                  : `${TOOL_KEYBINDING_LABELS[def.id]} (${shortcutLabel})`;
            return (
              <button
                key={def.id}
                ref={(el) => {
                  toolBtnRefs.current[def.id] = el;
                }}
                type="button"
                className={`wb-tool-icon-btn${isActive ? " is-active" : ""}${
                  dragToolId === def.id ? " is-dragging" : ""
                }`}
                title={title}
                aria-label={TOOL_KEYBINDING_LABELS[def.id]}
                aria-pressed={isActive}
                aria-expanded={
                  isActive && hasToolOptions(def.id === "shape" ? tool : def.id)
                    ? optionsOpen
                    : undefined
                }
                draggable={false}
                data-tool-id={def.id}
                style={
                  dragToolId === def.id
                    ? { transform: `translateY(${dragOffsetY}px)` }
                    : undefined
                }
                onPointerDown={(ev) => handleToolPointerDown(ev, def.id)}
                onClick={() => {
                  if (suppressToolClickRef.current) {
                    suppressToolClickRef.current = false;
                    return;
                  }
                  handleToolClick(def.id);
                }}
                onContextMenu={(ev) => {
                  if (dragToolId) ev.preventDefault();
                }}
              >
                <Icon />
                {dragToolId && dropToolId === def.id && dropToolId !== dragToolId ? (
                  <span
                    className={`wb-tool-icon-btn__drop-indicator${
                      orderedTools.findIndex((t) => t.id === dropToolId) <
                      orderedTools.findIndex((t) => t.id === dragToolId)
                        ? " wb-tool-icon-btn__drop-indicator--above"
                        : ""
                    }`}
                    aria-hidden="true"
                  />
                ) : null}
              </button>
            );
          })}
        </nav>
      </div>

      {showOptions
        ? createPortal(
            <div
              ref={popoverRef}
              className={`wb-tool-options-popover${
                isShapesGroupTool(tool) ? " wb-tool-options-popover--shapes" : ""
              }`}
              style={{
                top: popoverPos.top,
                left: popoverPos.left,
              }}
              role="dialog"
              aria-label={optionsLabel(tool)}
              // Глушим нажатие только на всплытии: на перехвате оно не доходило
              // до содержимого окна, и палитра цвета не ловила нажатие вовсе —
              // кружок насыщенности не двигался. Всплытие по-прежнему
              // останавливаем, чтобы доска не приняла нажатие на своё.
              onPointerDown={(ev) => ev.stopPropagation()}
            >
              {tool === "pen" ? <BoardPenOptions /> : null}
              {tool === "sticky" ? <BoardStickyOptions /> : null}
              {tool === "eraser" ? <BoardEraserOptions /> : null}
              {isShapesGroupTool(tool) ? (
                <>
                  <div
                    className="wb-tool-options-popover__variants"
                    aria-label="Тип фигуры"
                  >
                    <BoardShapeFlyout activeTool={tool} onPickTool={pickShapesGroupTool} />
                  </div>
                  <BoardShapeOptions />
                </>
              ) : null}
            </div>,
            document.body
          )
        : null}

      {widgetsOpen &&
      (onInsertWidget ||
        onInsertTimer ||
        onInsertCalculator ||
        onInsertGraphUnits ||
        onInsertCode ||
        onInsertSql) &&
      widgetsPos
        ? createPortal(
            <div
              ref={widgetsPopoverRef}
              className="wb-tool-options-popover wb-tool-options-popover--widgets"
              style={{
                top: widgetsPos.top,
                left: widgetsPos.left,
              }}
              role="dialog"
              aria-label="Виджеты"
              // Глушим нажатие только на всплытии: на перехвате оно не доходило
              // до содержимого окна, и палитра цвета не ловила нажатие вовсе —
              // кружок насыщенности не двигался. Всплытие по-прежнему
              // останавливаем, чтобы доска не приняла нажатие на своё.
              onPointerDown={(ev) => ev.stopPropagation()}
            >
              <BoardWidgetsFlyout
                onInsertPoll={
                  onInsertPoll
                    ? () => {
                        onInsertPoll();
                        setWidgetsOpen(false);
                      }
                    : undefined
                }
                onPick={(templateId) => {
                  onInsertWidget?.(templateId);
                  setWidgetsOpen(false);
                }}
                onInsertTimer={
                  onInsertTimer
                    ? () => {
                        onInsertTimer();
                        setWidgetsOpen(false);
                      }
                    : undefined
                }
                onInsertCalculator={
                  onInsertCalculator
                    ? () => {
                        onInsertCalculator();
                        setWidgetsOpen(false);
                      }
                    : undefined
                }
                onInsertGraphUnits={
                  onInsertGraphUnits
                    ? () => {
                        onInsertGraphUnits();
                        setWidgetsOpen(false);
                      }
                    : undefined
                }
                onInsertCode={
                  onInsertCode
                    ? () => {
                        onInsertCode();
                        setWidgetsOpen(false);
                      }
                    : undefined
                }
                onInsertSql={
                  onInsertSql
                    ? () => {
                        onInsertSql();
                        setWidgetsOpen(false);
                      }
                    : undefined
                }
              />
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
